// src/pages/cards.ts —— 06 C-05 用户卡片（含 C-28 暂扣 / 释放）、C-29 卡操作单、C-06 消费明细。
// 卡写动作 202 → 每 3s 轮询卡详情至 pending=null（最长 60s，TD-838 后台口径）；每个 await 之后的写入先过会话守卫，旧会话结果丢弃
import { ApiError } from '../../../shared/src/api/client';
import type { RouteKey } from '../../../shared/src/api/routes';
import type { Dict } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { cmp, fmtAmount, fmtOriginal, parseDecimalInput, toMicro } from '../../../shared/src/money';
import { createPoller, type Poller } from '../../../shared/src/poll';
import { fmtDateTime } from '../../../shared/src/time';
import { closeDrawer, onDrawerLeave, openConfirm, openDrawer, openForm, reloadCurrent, sessionGuard } from '../domains/table';
import { str, strArr, type AdminCtx } from '../domains/types';
import { I18N_CARDS, REWRITES_CARDS } from '../i18n/cards';
import type { AdminCardActionRow, AdminCardDetail, AdminCardSecret, AdminCardRow, AdminCardTx, AdminCardUpstream, AdminTxDetail, AdminTxRow, MockTransitionView, ReconcileResult, SubmitView } from './cards.types';
import { can } from '../perm';
import { openUser360 } from './shells';
import { chipMap, defineView, type ActionDef, type DrawerAction, type DrawerSpec, type ErasedView, type FormDef, type FormVals, type Need, type PageMap } from './types';
import { cell, chipOf, localErr, withBadge, type CellVals, type ChipKind } from './ui';

// TD-845：Mock 交易仅 dev / sandbox 构建出现；导出为可变对象供用例切换
export const CARD_FLAGS = { mock: import.meta.env.DEV || __GP_SANDBOX__ };

const POLL_MS = 3_000;
const POLL_MAX_MS = 60_000;
const HOLD_REVIEW_THRESHOLD = '1000'; // 06 C-28 cardHoldReviewThreshold 默认值；该键不经公共配置下发
const CARD_EDIT: Need = { module: 'cards', bit: 'edit' };
const CARD_APPROVE: Need = { module: 'cards', bit: 'approve' };
const CARD_SECRET = { module: 'cardSecret', bit: 'view' } as const; // 2026-10-17 后台卡面明文查看（独立矩阵模块，仅 view 位；无位 → 按钮不渲染）
export const REVEAL_MS = 30_000; // 明文展示时长：到点自动重新掩码
const REVEAL_REASON_MAX = 200;

// 原型 L513 FACES 逐字
const FACES: Readonly<Record<string, string>> = {
  black: 'linear-gradient(135deg,#0f1a17,#2a3833)',
  green: 'linear-gradient(135deg,#0b5c41,#39c88f)',
  purple: 'linear-gradient(135deg,#2e1f7a,#8a6cf5)',
  red: 'linear-gradient(135deg,#8a1a3b,#f07a4a)',
};

// 06 C-05 状态列（FAILED 取原型 stFailed「失败」）
const CARD_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = {
  ACTIVE: ['green', 'stActive'], INACTIVE: ['gray', 'stFrozen'], PENDING: ['amber', 'stPending'], RECHARGE: ['blue', 'stRecharge'],
  PRECLOSE: ['amber', 'stPreclose'], CLOSED: ['red', 'stClosed'], FAILED: ['red', 'stFailed'],
};
// 06 C-06 状态 9 态（PascalCase 原样，D28）；MailCode 文案为原值（原型 L719）；Unknown = 未识别上游值（存 raw 并告警），与卡操作单待对账无关
const TX_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = {
  Settle: ['green', 'stSettle'], Authorize: ['amber', 'stAuth'], AuthorizeDeclined: ['red', 'stDeclined'], Refund: ['blue', 'stRefund'],
  Dispute: ['red', 'stDispute'], Auth_Delete: ['gray', 'stAuthDel'], RefundCancell: ['gray', 'stRefundCancel'], MailCode: ['purple', 'MailCode'], Unknown: ['gray', 'stTxUnknown'],
};
// 06 C-29 状态列（card_actions.status 五值，cardq/admin.go facet 白名单）
const CA_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = {
  submitted: ['gray', 'caSubmitted'], upstream_accepted: ['blue', 'caAccepted'], unknown: ['amber', 'stUnknown'], success: ['green', 'caSuccess'], failed: ['red', 'caFailed'],
};
const STEP_STATUS_KEY: Readonly<Record<string, string>> = { pending: 'crdSsPending', sent: 'crdSsSent', accepted: 'crdSsAccepted', unknown: 'crdSsUnknown', waiting: 'crdSsWaiting' };

const pascal = (s: string): string => s.split('_').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');
const textOr = (t: Dict, key: string, fallback: string): string => str(t, key) || fallback;
const tz = (ctx: AdminCtx): string | undefined => ctx.s.sh.timezone || undefined;

// 金额：D8-12 截断展示；后端值非法时原样显示（不抛错）
function usd(v: string, dp = 2): string {
  try {
    return `$${fmtAmount(v, dp)}`;
  } catch {
    return `$${v}`;
  }
}

function micro(v: string): bigint | null {
  try {
    return toMicro(v);
  } catch {
    return null;
  }
}

const positive = (v: string | null): boolean => {
  const m = v === null ? null : micro(v);
  return m !== null && m > 0n;
};

function sameAmount(a: string | null, b: string): boolean {
  const x = a === null ? null : micro(a);
  const y = micro(b);
  return x === null || y === null || x === y; // 不可比较（非法值 / 未取到）不报不一致
}

// 两位小数输入 → 规范化两位串（"100" → "100.00"）；非法 / 超位数返回 null
function normAmount(x: string | boolean | undefined): string | null {
  const s = typeof x === 'string' ? x.trim() : '';
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const n = parseDecimalInput(s, 2);
  return n === null ? null : fmtAmount(n, 2, { group: false });
}

// 费率串去尾随 0（"1.500000" → "1.5"）；非法值原样
function trimDec(v: string): string {
  const n = parseDecimalInput(v, 6);
  if (n === null) return v;
  return n.includes('.') ? n.replace(/\.?0+$/, '') : n;
}

const textVal = (v: FormVals, k: string): string => {
  const x = v[k];
  return typeof x === 'string' ? x.trim() : '';
};

export const actLabel = (t: Dict, action: string): string => textOr(t, `act${pascal(action)}`, action);
const stepLabel = (t: Dict, step: string): string => textOr(t, `step${pascal(step)}`, step);
const stepStatusLabel = (t: Dict, s: string): string => textOr(t, STEP_STATUS_KEY[s] ?? '', s);
const chipText = (t: Dict, map: Readonly<Record<string, readonly [ChipKind, string]>>, v: string): string => {
  const hit = map[v];
  return hit ? textOr(t, hit[1], v) : v;
};
const userText = (u: { uid: string; email: string | null }): string => u.email ?? u.uid;
const brandMark = (brand: string): string => (brand === 'MASTERCARD' ? 'MC' : brand === 'VISA' ? 'VISA' : brand);

// facet 文案：product_id 以已渲染行的产品回填（`{bin} · {name}`），未见过的 id 显示原值；容量有界
const PRODUCT_LABELS = new Map<string, string>();
const PRODUCT_LABELS_MAX = 500;
function rememberProduct(p: AdminCardRow['product']): void {
  const k = String(p.id);
  if (PRODUCT_LABELS.has(k)) return;
  if (PRODUCT_LABELS.size >= PRODUCT_LABELS_MAX) {
    const first = PRODUCT_LABELS.keys().next();
    if (!first.done) PRODUCT_LABELS.delete(first.value);
  }
  PRODUCT_LABELS.set(k, `${p.bin} · ${p.name}`);
}
export const productFacetLabel = (_t: Dict, v: string): string => PRODUCT_LABELS.get(v) ?? v;
export const budgetFacetLabel = (_t: Dict, v: string): string => (/^\d+$/.test(v) ? `#${v}` : v);

// 写路径在途锁（每宿主 × 动作键）：同一动作在途时重复点击直接忽略，避免二次 POST（1212 / 重复 Mock / 重复对账）
const inflight = new WeakMap<object, Set<string>>();

// 抽屉按钮 / 行内直调动作：在途锁 + 同步 / 异步错误统一 toast；会话已换则静默（表单与确认框由 table 机制兜底）
function guarded(key: string, fn: (ctx: AdminCtx) => Promise<void>): (ctx: AdminCtx) => Promise<void> {
  return async (ctx) => {
    let keys = inflight.get(ctx.host);
    if (!keys) {
      keys = new Set();
      inflight.set(ctx.host, keys);
    }
    if (keys.has(key)) return;
    keys.add(key);
    const alive = sessionGuard(ctx);
    try {
      await fn(ctx);
    } catch (e) {
      if (alive()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    } finally {
      keys.delete(key);
    }
  };
}

// ---- 202 轮询（06 C-05：3s 间隔、最长 60s；切页 / 会话结束即停） ----

// 全局只保留一个轮询：60s 内对另一张卡写操作会接管轮询（前一张卡的结果由列表刷新呈现）
let activePoll: Poller | null = null;

export function stopCardPoll(): void {
  activePoll?.stop();
  activePoll = null;
}

const CARD_SPECS = new WeakMap<DrawerSpec, number>(); // 卡抽屉 spec → card id（刷新抽屉时按对象识别）

function isCardDrawerOf(ctx: AdminCtx, cardId: number): boolean {
  const d = ctx.host.state.tb.drawer;
  return d !== null && CARD_SPECS.get(d) === cardId;
}

export function pollUntilSettled(ctx: AdminCtx, cardId: number): void {
  stopCardPoll();
  const alive = sessionGuard(ctx);
  const started = Date.now();
  const page = ctx.s.page;
  const p: Poller = createPoller(async () => {
    const mine = (): boolean => activePoll === p;
    const end = (): void => {
      if (mine()) stopCardPoll();
    };
    if (!alive() || ctx.s.page !== page) return end();
    let d: AdminCardDetail;
    try {
      // 202 后的定时轮询：passive（触发它的写操作本身已是主动请求）
      d = await ctx.gp.api.call<AdminCardDetail>('GET /api/admin/cards/{id}', { path: { id: cardId }, passive: true });
    } catch (e) {
      end();
      if (alive()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
      return;
    }
    if (!alive() || !mine()) return end();
    if (!d.pending) {
      end();
      await reloadCurrent(ctx);
      if (alive() && isCardDrawerOf(ctx, cardId)) showCardDrawer(ctx, d);
      return;
    }
    if (Date.now() - started >= POLL_MAX_MS) {
      end();
      ctx.gp.ui.toast(str(ctx.t, 'stillRunning'));
    }
  }, POLL_MS);
  activePoll = p;
  p.start();
}

type CardWriteRoute =
  | 'POST /api/admin/cards/{id}/recharge'
  | 'POST /api/admin/cards/{id}/withdraw'
  | 'POST /api/admin/cards/{id}/freeze'
  | 'POST /api/admin/cards/{id}/unfreeze'
  | 'POST /api/admin/cards/{id}/close'
  | 'POST /api/admin/cards/{id}/hold'
  | 'POST /api/admin/cards/{id}/release';

// 抛出的 ApiError 由调用方（submitForm 红条 / doConfirm toast / guarded toast）按 TD-839 映射
export async function submitCardWrite(ctx: AdminCtx, route: CardWriteRoute, id: number, body: Readonly<Record<string, string>>, toastKey: string, suffix = ''): Promise<void> {
  const alive = sessionGuard(ctx);
  const r = await ctx.gp.api.call<SubmitView>(route, { path: { id }, body });
  if (!alive()) return;
  ctx.gp.ui.toast(str(ctx.t, toastKey) + suffix);
  if (isCardDrawerOf(ctx, id)) closeDrawer(ctx);
  pollUntilSettled(ctx, r.card_id);
}

// ---- C-05 表单（代增加额度 / 代卡转出）与 C-28 表单（暂扣 / 释放） ----

export function amountForm(ctx: AdminCtx, row: AdminCardRow, kind: 'recharge' | 'withdraw'): FormDef {
  const t = ctx.t;
  const route: CardWriteRoute = kind === 'recharge' ? 'POST /api/admin/cards/{id}/recharge' : 'POST /api/admin/cards/{id}/withdraw';
  const avail = micro(row.available);
  const init = kind === 'recharge' ? '100' : fmtAmount(avail !== null && avail < 50_000_000n ? avail : 50_000_000n, 2, { group: false });
  return {
    title: interpolate(str(t, kind === 'recharge' ? 'cardTopupTitle' : 'cardWithdrawTitle'), { card: row.card_no }),
    tip: str(t, 'cardOpTip'),
    fields: [{ k: 'amount', label: `${str(t, 'cAmount')} (USD)`, type: 'number' }],
    init: { amount: init },
    validate: (v) => {
      const norm = normAmount(v['amount']);
      if (norm === null) return str(t, 'crdErrAmount');
      if (kind === 'recharge' && cmp(toMicro(norm), toMicro('1')) < 0) return str(t, 'errAmountMin1');
      if (kind === 'withdraw' && !positive(norm)) return str(t, 'crdErrAmount');
      return '';
    },
    submit: async (c, v) => {
      const amount = normAmount(v['amount']);
      if (amount === null) throw localErr(route, str(t, 'crdErrAmount'));
      await submitCardWrite(c, route, row.id, { amount }, kind === 'recharge' ? 'toastRecharge' : 'toastWithdraw');
    },
  };
}

// 测试环境 Mock 交易（TD-845，仅 dev / sandbox 构建）：金额 + 商户名 + 后续状态；默认「授权 → 结算」以触发计费与扣费通知。
export const MOCK_TRANSITIONS = ['Settle', 'none', 'Auth_Delete', 'Refund', 'Dispute'] as const;
export function mockForm(ctx: AdminCtx, d: AdminCardDetail): FormDef {
  const t = ctx.t;
  return {
    title: interpolate(str(t, 'cardMockTitle'), { card: d.card_no }),
    tip: str(t, 'cardMockTip'),
    fields: [
      { k: 'amount', label: `${str(t, 'cAmount')} (USD)`, type: 'number' },
      { k: 'merchant', label: str(t, 'cardMockMerchant'), type: 'text', optional: true, placeholder: 'GoodPayU Test Shop' },
      { k: 'transition', label: str(t, 'cardMockTransition'), type: 'select', options: MOCK_TRANSITIONS, optionLabel: (dict, v) => str(dict, `cardMockTr_${v}`) || v },
    ],
    init: { amount: '1', merchant: '', transition: 'Settle' },
    validate: (v) => {
      const norm = normAmount(v['amount']);
      if (norm === null || !positive(norm)) return str(t, 'crdErrAmount');
      const m = typeof v['merchant'] === 'string' ? v['merchant'].trim() : '';
      if (m.length > 255) return str(t, 'cardMockMerchantTooLong');
      return '';
    },
    submit: async (c, v) => {
      const amount = normAmount(v['amount']);
      if (amount === null) throw localErr('POST /api/admin/cards/{id}/mock-transaction', str(t, 'crdErrAmount'));
      const merchant = typeof v['merchant'] === 'string' ? v['merchant'].trim() : '';
      const transition = typeof v['transition'] === 'string' ? v['transition'] : 'Settle';
      const body: Record<string, string> = { status: 'Authorize', amount };
      if (merchant !== '') body['merchant_name'] = merchant;
      if (transition !== 'none') body['transition'] = transition;
      const alive = sessionGuard(c);
      await c.gp.api.call('POST /api/admin/cards/{id}/mock-transaction', { path: { id: d.id }, body });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastMock'));
      closeDrawer(c);
      c.host.setState({ page: 'logs', tab: 0 }); // 原型 L1009 mockTx：跳回调日志页查看 card.transaction.update
    },
  };
}

// 对已有交易的 Mock 状态流转（仅 dev / sandbox 构建）：Authorize → 撤销授权 / 结算；Settle → 退款 / 争议（与后端 4220 口径一致）。
export const MOCK_TRANSITION_TARGETS: Readonly<Record<string, readonly string[]>> = {
  Authorize: ['Auth_Delete', 'Settle'],
  Settle: ['Refund', 'Dispute'],
};

export const mockTransitionTargets = (status: string): readonly string[] => MOCK_TRANSITION_TARGETS[status] ?? [];

export function mockTransitionForm(ctx: AdminCtx, cardId: number, x: AdminCardTx): FormDef {
  const t = ctx.t;
  const route: RouteKey = 'POST /api/admin/card-transactions/{id}/mock-transition';
  const targets = mockTransitionTargets(x.status);
  return {
    title: interpolate(str(t, 'mockTrTitle'), { tx: x.transaction_id }),
    tip: str(t, 'mockTrTip'),
    fields: [
      { k: 'status', label: str(t, 'mockTrStatus'), type: 'select', options: targets, optionLabel: (dict, v) => str(dict, `mockTrTo_${v}`) || v },
      { k: 'amount', label: str(t, 'mockTrAmount'), type: 'number', optional: true },
    ],
    init: { status: targets[0] ?? '', amount: '' },
    validate: (v) => {
      if (!targets.includes(String(v['status']))) return str(t, 'mockTrStatus');
      const raw = textVal(v, 'amount');
      if (raw === '') return '';
      const norm = normAmount(raw);
      return norm === null || !positive(norm) ? str(t, 'crdErrAmount') : '';
    },
    submit: async (c, v) => {
      const status = String(v['status']);
      if (!targets.includes(status)) throw localErr(route, str(t, 'mockTrStatus'));
      const raw = textVal(v, 'amount');
      const amount = raw === '' ? null : normAmount(raw);
      if (raw !== '' && amount === null) throw localErr(route, str(t, 'crdErrAmount'));
      const body: Record<string, string> = amount === null ? { status } : { status, amount };
      const alive = sessionGuard(c);
      await c.gp.api.call<MockTransitionView>(route, { path: { id: x.id }, body });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastMockTr'));
      if (isCardDrawerOf(c, cardId)) openCardDrawer(c, cardId);
    },
  };
}

export function holdForm(ctx: AdminCtx, d: AdminCardDetail, kind: 'hold' | 'release'): FormDef {
  const t = ctx.t;
  const route: CardWriteRoute = kind === 'hold' ? 'POST /api/admin/cards/{id}/hold' : 'POST /api/admin/cards/{id}/release';
  const tip = interpolate(str(t, 'holdTip'), { total: usd(d.total_limit), held: usd(d.held), upstream: usd(d.upstream_limit), spent: usd(d.spent) });
  return {
    title: interpolate(str(t, kind === 'hold' ? 'cardHoldTitle' : 'cardReleaseTitle'), { card: d.card_no }),
    tip: kind === 'hold' ? `${tip} ${interpolate(str(t, 'holdReviewTip'), { threshold: usd(HOLD_REVIEW_THRESHOLD, 0) })}` : tip,
    fields: [
      { k: 'amount', label: `${str(t, 'cAmount')} (USD)`, type: 'number' },
      { k: 'reason', label: str(t, 'cReason'), type: 'text', wide: true },
    ],
    init: { amount: '', reason: '' },
    validate: (v) => {
      const amount = normAmount(v['amount']);
      if (amount === null || !positive(amount)) return str(t, 'crdErrAmount');
      const held = micro(d.held);
      if (kind === 'release' && held !== null && toMicro(amount) > held) return str(t, 'errReleaseOverHeld');
      const reason = textVal(v, 'reason');
      if (reason.length < 1 || [...reason].length > 255) return str(t, 'errReason');
      return '';
    },
    submit: async (c, v) => {
      const amount = normAmount(v['amount']);
      if (amount === null) throw localErr(route, str(t, 'crdErrAmount'));
      const review = kind === 'hold' && toMicro(amount) > toMicro(HOLD_REVIEW_THRESHOLD);
      await submitCardWrite(c, route, d.id, { amount, reason: textVal(v, 'reason') }, kind === 'hold' ? 'toastHold' : 'toastRelease', review ? ` · ${str(t, 'crdPendingReview')}` : '');
    },
  };
}

// S-70：立即跑一轮认领；结论按 outcome 出文案，未登记值回落 toastReconcile
async function reconcile(ctx: AdminCtx, bizNo: string, cardId: number | null): Promise<void> {
  const alive = sessionGuard(ctx);
  const r = await ctx.gp.api.call<ReconcileResult>('POST /api/admin/card-actions/{biz_no}/reconcile', { path: { biz_no: bizNo }, body: {} });
  if (!alive()) return;
  ctx.gp.ui.toast(textOr(ctx.t, `reconOutcome${pascal(r.outcome)}`, str(ctx.t, 'toastReconcile')));
  await reloadCurrent(ctx);
  if (alive() && cardId !== null && isCardDrawerOf(ctx, cardId)) openCardDrawer(ctx, cardId);
}

// ---- C-05 卡面明文查看（2026-10-17；POST /api/admin/cards/{id}/secret，cardSecret.view） ----
// 明文只存本模块 WeakMap（按宿主），不进 state 以外的任何存储；30s 到点、抽屉关闭 / 切卡 / 会话复位时立即丢弃并重渲染为掩码。

interface Revealed {
  cardId: number;
  secret: AdminCardSecret;
  timer: ReturnType<typeof setTimeout>;
}

const revealed = new WeakMap<object, Revealed>();

// 丢弃明文：清定时器、覆写字段（尽力缩短引用存活）后删除
export function clearReveal(host: object): void {
  const r = revealed.get(host);
  if (!r) return;
  clearTimeout(r.timer);
  r.secret = { card_number: '', cvv: '', expiry: '' };
  revealed.delete(host);
}

const revealedOf = (ctx: AdminCtx, cardId: number): AdminCardSecret | null => {
  const r = revealed.get(ctx.host);
  return r && r.cardId === cardId ? r.secret : null;
};

// 卡抽屉 spec → 渲染输入（明文到达 / 到期时按当前抽屉的同一份详情与上游状态重渲染）
const CARD_VIEWS = new WeakMap<DrawerSpec, { d: AdminCardDetail; up: UpstreamState }>();

function rerenderCard(ctx: AdminCtx, cardId: number): void {
  const cur = ctx.host.state.tb.drawer;
  const v = cur ? CARD_VIEWS.get(cur) : undefined;
  if (v && v.d.id === cardId) openDrawer(ctx, cardDrawer(ctx, v.d, v.up));
}

// 4 位一组展示（复制用原串）
const groupPan = (pan: string): string => pan.replace(/(\d{4})(?=\d)/g, '$1 ');

export function revealForm(ctx: AdminCtx, d: AdminCardDetail): FormDef {
  const t = ctx.t;
  const route: RouteKey = 'POST /api/admin/cards/{id}/secret';
  const check = (v: FormVals): string => {
    if (!/^\d{6}$/.test(textVal(v, 'totp'))) return str(t, 'rvlErrTotp');
    const reason = textVal(v, 'reason');
    if (reason === '' || [...reason].length > REVEAL_REASON_MAX) return str(t, 'rvlErrReason');
    return '';
  };
  return {
    title: interpolate(str(t, 'rvlTitle'), { card: d.card_no }),
    tip: str(t, 'rvlTip'),
    fields: [
      { k: 'totp', label: str(t, 'rvlTotp'), type: 'text', placeholder: str(t, 'rvlTotpPh') },
      { k: 'reason', label: str(t, 'rvlReason'), type: 'text', wide: true, placeholder: str(t, 'rvlReasonPh') },
    ],
    init: { totp: '', reason: '' },
    validate: check,
    submit: async (c, v) => {
      const bad = check(v);
      if (bad) throw localErr(route, bad);
      const alive = sessionGuard(c);
      let secret: AdminCardSecret;
      try {
        secret = await c.gp.api.call<AdminCardSecret>(route, { path: { id: d.id }, body: { totp: textVal(v, 'totp'), reason: textVal(v, 'reason') } });
      } catch (e) {
        if (e instanceof ApiError && e.code === 1205) throw localErr(route, str(t, 'rvlErrState'));
        if (e instanceof ApiError && e.code === 4220 && e.data.field === 'reason') throw localErr(route, str(t, 'rvlErrReason'));
        throw e;
      }
      // 会话已换 / 抽屉已关闭或已切到别的卡：明文直接丢弃，不展示
      if (!alive() || !isCardDrawerOf(c, d.id)) return;
      clearReveal(c.host);
      const timer = setTimeout(() => {
        const r = revealed.get(c.host);
        if (!r || r.timer !== timer) return;
        clearReveal(c.host);
        if (alive()) rerenderCard(c, d.id);
      }, REVEAL_MS);
      revealed.set(c.host, { cardId: d.id, secret, timer });
      rerenderCard(c, d.id);
    },
  };
}

// ---- C-05 卡抽屉 ----

// 上游对照异步状态：loading 先渲染本地数据；failed = 接口本身失败 / 超时；done = 接口返回（可能带 upstream_error）
export type UpstreamState = { kind: 'loading' } | { kind: 'failed' } | { kind: 'done'; r: AdminCardUpstream };
const UP_LOADING: UpstreamState = { kind: 'loading' };

const upstreamRetryable = (up: UpstreamState): boolean => up.kind === 'failed' || (up.kind === 'done' && up.r.upstream_error !== null);

function upstreamField(t: Dict, d: AdminCardDetail, s: UpstreamState): { k: string; v: string; wide: boolean } {
  const label = str(t, 'limitUpstream');
  if (s.kind === 'loading') return { k: label, v: str(t, 'upLoading'), wide: true };
  if (s.kind === 'failed') return { k: label, v: str(t, 'upNoResponse'), wide: true };
  const r = s.r;
  if (r.upstream_error === 'unavailable') return { k: `${label}（${str(t, 'crdUpUnavailable')}）`, v: str(t, 'upNoResponse'), wide: true };
  if (r.upstream_error) return { k: `${label}（${str(t, 'crdUpRejected')}）`, v: str(t, 'upReadErr'), wide: true };
  const up = r.upstream;
  if (!up) return { k: label, v: '—', wide: true };
  // 06 C-05：两侧 limit / spent / available 不一致仅提示（以对账任务告警 card_limit_drift 为准）
  const drift = !sameAmount(up.limit, d.upstream_limit) || !sameAmount(up.spent, d.spent) || !sameAmount(up.available, d.available);
  const v = `${str(t, 'limitTotal')} ${up.limit === null ? '—' : usd(up.limit)} / ${str(t, 'limitSpent')} ${usd(up.spent)} / ${str(t, 'limitAvailable')} ${up.available === null ? '—' : usd(up.available)}`;
  return { k: `${label}${drift ? ' ⚠' : ''}`, v, wide: true };
}

export function cardDrawer(ctx: AdminCtx, d: AdminCardDetail, up: UpstreamState = UP_LOADING): DrawerSpec {
  const t = ctx.t;
  const z = tz(ctx);
  const live = d.status === 'ACTIVE' || d.status === 'INACTIVE';
  const idle = d.pending === null;
  const unknown = d.pending?.status === 'unknown';
  const f = d.product_fees;
  const mark = (b: boolean): string => (b ? '✓' : '✕');
  const actions: DrawerAction[] = [];
  const push = (on: boolean, a: DrawerAction): void => {
    if (on) actions.push(a);
  };
  // 06 C-05 底部操作序：销卡 / Mock / 冻结|解冻 / 暂扣 / 释放 / 手动对账 / 代卡转出 / 代增加额度；能力位为 0 的动作隐藏（D23）
  push(idle && live && d.capabilities.close, {
    label: str(t, 'aCloseCard'), kind: 'danger', need: CARD_EDIT,
    run: (c) => openConfirm(c, { title: str(t, 'closeCardTitle'), text: str(t, 'closeCardText'), label: d.card_no, run: (cc) => submitCardWrite(cc, 'POST /api/admin/cards/{id}/close', d.id, {}, 'toastCloseCard') }),
  });
  push(CARD_FLAGS.mock && d.status === 'ACTIVE', { label: str(t, 'aMock'), kind: '', need: CARD_EDIT, run: (c) => openForm(c, mockForm(c, d)) });
  push(idle && live && d.capabilities.freeze, {
    label: str(t, d.status === 'INACTIVE' ? 'aUnfreeze' : 'aFreeze'), kind: '', need: CARD_EDIT,
    run: guarded(`write:${d.id}`, (c) =>
      d.status === 'INACTIVE'
        ? submitCardWrite(c, 'POST /api/admin/cards/{id}/unfreeze', d.id, {}, 'toastUnfrozen')
        : submitCardWrite(c, 'POST /api/admin/cards/{id}/freeze', d.id, {}, 'toastFrozen'),
    ),
  });
  push(idle && live, { label: str(t, 'aHold'), kind: '', need: CARD_EDIT, run: (c) => openForm(c, holdForm(c, d, 'hold')) });
  push(idle && live && positive(d.held), { label: str(t, 'aRelease'), kind: '', need: CARD_EDIT, run: (c) => openForm(c, holdForm(c, d, 'release')) });
  push(unknown, { label: str(t, 'aReconcile'), kind: '', need: CARD_EDIT, run: guarded(`reconcile:${d.pending?.biz_no ?? ''}`, (c) => reconcile(c, d.pending?.biz_no ?? '', d.id)) });
  // 方案 A：快照未核对也可发起卡转出——服务端落单前即时核对（交易推导），冷静期 / 未核对以 1207 + reason 拒绝。
  push(idle && live, { label: str(t, 'aWithdraw'), kind: '', need: CARD_EDIT, run: (c) => openForm(c, amountForm(c, d, 'withdraw')) });
  push(idle && d.status === 'ACTIVE', { label: str(t, 'aRecharge'), kind: 'green', need: CARD_EDIT, run: (c) => openForm(c, amountForm(c, d, 'recharge')) });
  push(upstreamRetryable(up), { label: str(t, 'aUpRetry'), kind: '', run: (c) => showCardDrawer(c, d) });
  // 卡面明文：无 cardSecret.view 不渲染按钮（非置灰）；仅 ACTIVE / INACTIVE 可看（与后端 1205 口径一致）
  const secret = revealedOf(ctx, d.id);
  if (secret) {
    actions.unshift(
      { label: str(t, 'aRevealHide'), kind: 'primary', run: (c) => { clearReveal(c.host); rerenderCard(c, d.id); } },
      { label: str(t, 'aRevealCopyPan'), kind: '', run: (c) => c.gp.ui.copy(secret.card_number) },
      ...(secret.expiry ? [{ label: str(t, 'aRevealCopyExpiry'), kind: '' as const, run: (c: AdminCtx) => c.gp.ui.copy(secret.expiry) }] : []),
      { label: str(t, 'aRevealCopyCvv'), kind: '', run: (c) => c.gp.ui.copy(secret.cvv) },
    );
  } else {
    push(live && can(ctx.s.sh.me, CARD_SECRET.module, CARD_SECRET.bit), { label: str(t, 'aRevealCard'), kind: '', run: (c) => openForm(c, revealForm(c, d)) });
  }

  const fields: Array<{ k: string; v: string; wide?: boolean }> = [
    ...(secret
      ? [
          { k: str(t, 'rvlPan'), v: groupPan(secret.card_number), wide: true },
          { k: str(t, 'rvlExpiry'), v: secret.expiry || (d.expiry ?? '--/--') },
          { k: str(t, 'rvlCvv'), v: secret.cvv },
          { k: str(t, 'rvlAutoHide'), v: interpolate(str(t, 'rvlAutoHideV'), { n: String(REVEAL_MS / 1000) }), wide: true },
        ]
      : []),
    { k: str(t, 'cHolder'), v: `${d.holder_first} ${d.holder_last}`.trim() || d.holder_name },
    { k: str(t, 'cBillingAddr'), v: [d.holder_street, d.holder_city, d.holder_state, d.holder_zip, d.holder_country].filter((x) => x).join(', ') || '—', wide: true },
    { k: str(t, 'cUser'), v: userText(d.user) },
    { k: str(t, 'cStatus'), v: d.status }, // 原型 L665 抽屉状态为原值
    { k: str(t, 'cUpstreamId'), v: d.upstream_card_id === null ? '—' : String(d.upstream_card_id) },
    { k: str(t, 'cBudget'), v: d.budget ? `#${d.budget.id} · ${str(t, 'upstreamWord')} ${d.budget.upstream_budget_id ?? '—'}` : '—' },
    { k: str(t, 'cOpened'), v: fmtDateTime(d.opened_at ?? d.created_at, { tz: z }) },
    { k: str(t, 'cProduct'), v: `${d.product.bin} · ${d.product.area}` },
    { k: str(t, 'cExpiry'), v: d.expiry ?? '--/--' },
    { k: str(t, 'crdCaps'), v: `${str(t, 'aFreeze')} ${mark(d.capabilities.freeze)} · ${str(t, 'aCloseCard')} ${mark(d.capabilities.close)}` },
    {
      k: str(t, 'crdFees'), wide: true,
      v: `${str(t, 'crdFeeOpen')} ${usd(f.open_fee)} · ${str(t, 'crdFeeClose')} ${usd(f.close_fee)} · ${str(t, 'crdFeeTx')} ${trimDec(f.tx_fee_rate)}% + ${usd(f.tx_fee_fixed)} · ${str(t, 'crdFeeCross')} ${trimDec(f.cross_border_rate)}% + ${usd(f.cross_border_fixed)} · ${str(t, 'crdFeeDeclined')} ${usd(f.declined_fee)} · ${str(t, 'crdFeeDispute')} ${usd(f.dispute_fee)}`,
    },
    { k: str(t, 'limitLocal'), v: `${str(t, 'limitTotal')} ${usd(d.limits.total)} / ${str(t, 'limitSpent')} ${d.spent_verified ? usd(d.limits.spent) : `≥${usd(d.limits.spent)}`} / ${str(t, 'limitAvailable')} ${d.spent_verified ? usd(d.limits.available) : `≤${usd(d.limits.available)}`} · ${str(t, 'cHeld')} ${usd(d.held)} · ${str(t, 'upLimit')} ${usd(d.upstream_limit)}`, wide: true },
    ...(!d.spent_verified && d.status !== 'PENDING' ? [{ k: str(t, 'spentUnverified'), v: str(t, 'spentUnverified'), wide: true }] : []),
    upstreamField(t, d, up),
    ...(d.status === 'CLOSED' ? [{ k: str(t, 'crdFinal'), v: `${d.final_spent === null ? '—' : usd(d.final_spent)} / ${d.final_committed === null ? '—' : usd(d.final_committed)} · ${fmtDateTime(d.closed_at, { tz: z })}`, wide: true }] : []),
    ...(d.spent_dirty_at ? [{ k: str(t, 'spentDirty'), v: fmtDateTime(d.spent_dirty_at, { tz: z }), wide: true }] : []),
  ];
  const spec: DrawerSpec = {
    kind: str(t, 'cards'),
    title: d.card_no,
    subtitle: `${userText(d.user)} · ${d.product.name}${unknown ? ` · ${str(t, 'pendingUnknownTip')} ${d.pending?.biz_no ?? ''}` : ''}`,
    card: {
      // 原型 L664 卡面样式逐字；卡面恒只出 card_no（R-10）。完整卡号 / CVV 仅经「显示卡号」（cardSecret.view + 2FA + 原因）出现在字段格，30s 自动掩码
      style: `background:${FACES[d.face_color] ?? FACES['black'] ?? ''};color:#fff;border-radius:16px;padding:18px;display:flex;flex-direction:column;gap:22px`,
      balance: d.spent_verified ? `$${fmtAmountSafe(d.available)}` : `≤$${fmtAmountSafe(d.available)}`,
      brand: brandMark(d.product.brand),
      no: d.card_no,
    },
    fields,
    list: {
      title: `${str(t, 'cardTx')} · ${str(t, 'smsCodes')}`,
      rows: [
        ...d.transactions.map((x) => {
          // 测试环境：Authorize / Settle 行可点开「Mock 流转」表单（撤销授权 / 结算；退款 / 争议），需 cards.edit（后端 1501 兜底）
          const mockTr = CARD_FLAGS.mock && mockTransitionTargets(x.status).length > 0 && can(ctx.s.sh.me, CARD_EDIT.module, CARD_EDIT.bit);
          return {
            left: x.merchant_name ?? '—',
            sub: `${fmtDateTime(x.transaction_date, { tz: z })}${origSuffix(x)}${positive(x.fee_charged) && x.fee_charged !== null ? ` · fee ${usd(x.fee_charged)}` : ''}${mockTr ? ` · ${str(t, 'aMockTransition')} ›` : ''}`,
            right: x.billing_amount === null ? '—' : `${x.transaction_type === 'Refund' ? '+' : '-'}${usd(x.billing_amount)}`,
            rightSub: chipText(t, TX_STATUS, x.status),
            tone: x.status === 'AuthorizeDeclined' ? ('bad' as const) : x.status === 'Authorize' ? ('warn' as const) : ('good' as const),
            ...(mockTr ? { go: (c: AdminCtx) => openForm(c, mockTransitionForm(c, d.id, x)) } : {}),
          };
        }),
        ...d.sms_codes.map((s) => ({ left: `3DS ${s.code}`, sub: s.merchant ?? '', right: '', rightSub: `MailCode · ${fmtDateTime(s.created_at, { tz: z })}` })),
      ],
    },
    // DrawerSpec.timeline 只有 { label, time }：状态以文案承载（06 C-05 圆点着色不落地，W-8）
    timeline: d.timeline.map((n) => ({ label: `${actLabel(t, n.action)} · ${chipText(t, CA_STATUS, n.status)}`, time: fmtDateTime(n.finalized_at ?? n.created_at, { tz: z }) })),
    actions,
  };
  CARD_SPECS.set(spec, d.id);
  CARD_VIEWS.set(spec, { d, up });
  // 抽屉关闭 / 切到别的卡 / 会话复位：立即丢弃明文（同卡重渲染——上游补齐、轮询刷新、明文到达——保留）
  onDrawerLeave(spec, (next) => {
    if (next === null || CARD_SPECS.get(next) !== d.id) clearReveal(ctx.host);
  });
  return spec;
}

function fmtAmountSafe(v: string): string {
  try {
    return fmtAmount(v, 2);
  } catch {
    return v;
  }
}

// 下钻入口（C-13 产品抽屉用户卡片、C-29 / C-30 卡号列）；签名受契约锁约束（TD-851）。
// 同一宿主连续打开时只认最后一次；会话已换则丢弃
const drawerSeq = new WeakMap<object, number>();

// 上游对照异步补齐：先以 loading 渲染本地详情，再取 GET /cards/{id}/upstream；同一宿主只认最后一次请求，
// 且到达时抽屉仍须是同一张卡（已切卡 / 已关闭 / 会话已换 → 丢弃迟到结果）。失败（网络 / 超时）显示「上游暂时无响应」+ 重试。
const upstreamSeq = new WeakMap<object, number>();

export function showCardDrawer(ctx: AdminCtx, d: AdminCardDetail): void {
  const alive = sessionGuard(ctx);
  const seq = (upstreamSeq.get(ctx.host) ?? 0) + 1;
  upstreamSeq.set(ctx.host, seq);
  openDrawer(ctx, cardDrawer(ctx, d, UP_LOADING));
  const settle = (s: UpstreamState): void => {
    if (alive() && upstreamSeq.get(ctx.host) === seq && isCardDrawerOf(ctx, d.id)) openDrawer(ctx, cardDrawer(ctx, d, s));
  };
  void ctx.gp.api
    .call<AdminCardUpstream>('GET /api/admin/cards/{id}/upstream', { path: { id: d.id } })
    .then((r) => settle({ kind: 'done', r }))
    .catch(() => settle({ kind: 'failed' }));
}

export function openCardDrawer(ctx: AdminCtx, cardId: number): void {
  const alive = sessionGuard(ctx);
  const seq = (drawerSeq.get(ctx.host) ?? 0) + 1;
  drawerSeq.set(ctx.host, seq);
  void ctx.gp.api
    .call<AdminCardDetail>('GET /api/admin/cards/{id}', { path: { id: cardId } })
    .then((d) => {
      if (alive() && drawerSeq.get(ctx.host) === seq) showCardDrawer(ctx, d);
    })
    .catch((e: unknown) => {
      if (alive() && drawerSeq.get(ctx.host) === seq) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    });
}

// ---- C-05 列表 ----

function cardCells(ctx: AdminCtx, r: AdminCardRow): readonly CellVals[] {
  const t = ctx.t;
  rememberProduct(r.product);
  const st = chipOf(chipMap(t, CARD_STATUS), r.status, 'card.status');
  const pend = r.pending;
  // 在途提示：badge（text / title = biz_no）为页定义契约出口；状态章模板分支（S-38）只渲染副文本，故同时写入 sub
  const pendText = pend ? (pend.status === 'unknown' ? str(t, 'stUnknown') : `${str(t, 'stInflight')} · ${actLabel(t, pend.action)}`) : '';
  const status = pend ? { ...withBadge(st, pend.status === 'unknown' ? 'amber' : 'gray', pendText, pend.biz_no), sub: `${pendText} · ${pend.biz_no}` } : st;
  const held = positive(r.held);
  const heldCell = cell(held ? usd(r.held) : '—', held ? `${str(t, 'upLimit')} ${usd(r.upstream_limit)}` : '');
  return [
    cell(r.card_no, '', true),
    cell(userText(r.user)),
    cell(r.holder_name),
    cell(usd(r.total_limit)),
    cell(r.spent_verified || r.status === 'PENDING' ? usd(r.available) : `≤${usd(r.available)}`, `${str(t, 'cSpent')} ${r.spent_verified ? usd(r.spent) : `≥${usd(r.spent)} · ${str(t, 'spentUnverified')}`}`),
    held ? { ...heldCell, style: `${heldCell.style};color:#c47a0c` } : heldCell,
    cell(`${r.product.bin} · ${r.product.area}`, r.product.name),
    cell(r.budget_id === null ? '—' : `#${r.budget_id}`, '', true),
    status,
    cell(r.upstream_card_id === null ? '—' : String(r.upstream_card_id), '', true),
    cell(fmtDateTime(r.opened_at ?? r.created_at, { tz: tz(ctx) })),
  ];
}

// T13b ActionDef 无独立置灰位：在途时隐藏写动作，由状态章徽标给出 biz_no（06 C-05「置灰 + title」等价落地，W-8）
function cardRowActions(): readonly ActionDef<AdminCardRow>[] {
  return [
    { label: 'aDetail', run: (c, r) => openCardDrawer(c, r.id) },
    { label: 'aRecharge', need: CARD_EDIT, show: (r) => r.status === 'ACTIVE' && r.pending === null, run: (c, r) => openForm(c, amountForm(c, r, 'recharge')) },
    { label: 'aWithdraw', need: CARD_EDIT, show: (r) => (r.status === 'ACTIVE' || r.status === 'INACTIVE') && r.pending === null, run: (c, r) => openForm(c, amountForm(c, r, 'withdraw')) },
  ];
}

const CARD_TABS = ['all', 'ACTIVE', 'INACTIVE', 'PENDING', 'RECHARGE', 'PRECLOSE', 'CLOSED', 'FAILED'] as const;

function cardsView(key: (typeof CARD_TABS)[number]): ErasedView {
  return defineView<AdminCardRow>({
    key,
    source: { route: 'GET /api/admin/cards', tabParam: 'tab' },
    echo: '/admin/cards',
    cols: [
      { label: 'cCardNo' }, { label: 'cUser' }, { label: 'cHolder' }, { label: 'cTotalLimit' }, { label: 'cAvailable' }, { label: 'cHeld' },
      { label: 'cProduct', facet: 'product_id', facetLabel: productFacetLabel }, { label: 'cBudget', facet: 'budget_id', facetLabel: budgetFacetLabel }, { label: 'cStatus' },
      { label: 'cUpstreamId' }, { label: 'cOpened', sortKey: 'opened_at' },
    ],
    cells: cardCells,
    open: (c, r) => openCardDrawer(c, r.id),
    rowActions: () => cardRowActions(),
    csvName: 'cards',
  });
}

// ---- C-29 卡操作单 ----

function operatorText(t: Dict, r: AdminCardActionRow): string {
  const kind = r.operator.split(':')[0] ?? '';
  if (kind === 'user') return str(t, 'crdOpUser') || r.operator;
  if (kind === 'admin') return `${str(t, 'crdOpAdmin')} #${r.operator_admin_id ?? r.operator.slice(6)}`;
  if (kind === 'bot') return 'Bot';
  if (kind === 'system') return str(t, 'crdOpSystem') || r.operator;
  return r.operator;
}

function rejectForm(row: AdminCardActionRow, t: Dict): FormDef {
  return {
    title: `${str(t, 'aReject')} · ${row.biz_no}`,
    // 必填由 validate 给出 errNoteRequired（通用必填红条不适用）
    fields: [{ k: 'note', label: str(t, 'crdNote'), type: 'text', wide: true, optional: true }],
    init: { note: '' },
    validate: (v) => {
      const note = textVal(v, 'note');
      if (!note) return str(t, 'errNoteRequired');
      return [...note].length > 255 ? str(t, 'errReason') : '';
    },
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      await c.gp.api.call('POST /api/admin/card-actions/{biz_no}/reject', { path: { biz_no: row.biz_no }, body: { note: textVal(v, 'note') } });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastReject'));
      await reloadCurrent(c);
    },
  };
}

// 上游卡 id：纯数字且为安全整数（超出 2^53 的串经 Number() 会静默失真）
const safeId = (v: string): boolean => /^\d+$/.test(v) && Number.isSafeInteger(Number(v));

const outcomeLabel = (t: Dict, v: string): string => (v === 'success' ? textOr(t, 'crdOutcomeSuccess', v) : v === 'failed' ? textOr(t, 'crdOutcomeFailed', v) : v);

function resolveForm(row: AdminCardActionRow, t: Dict): FormDef {
  const needCard = row.step === 'create_card';
  const route: RouteKey = 'POST /api/admin/card-actions/{biz_no}/resolve';
  return {
    title: `${str(t, 'aResolve')} · ${row.biz_no}`,
    tip: str(t, 'resolveTip'),
    fields: [
      { k: 'outcome', label: str(t, 'cOutcome'), type: 'select', options: ['success', 'failed'], optionLabel: outcomeLabel },
      { k: 'reason', label: str(t, 'cReason'), type: 'text', wide: true },
      ...(needCard ? [{ k: 'upstream_card_id', label: str(t, 'cUpstreamId'), type: 'text' as const, optional: true }] : []),
    ],
    init: { outcome: 'failed', reason: '', ...(needCard ? { upstream_card_id: '' } : {}) },
    validate: (v) => {
      const reason = textVal(v, 'reason');
      if ([...reason].length < 10 || [...reason].length > 255) return str(t, 'errReason10');
      const up = textVal(v, 'upstream_card_id');
      // 建卡判成功须填；任何结论下填了就须是安全整数
      if (needCard && ((v['outcome'] === 'success' && up === '') || (up !== '' && !safeId(up)))) return str(t, 'errUpstreamCardId');
      return '';
    },
    submit: async (c, v) => {
      const up = textVal(v, 'upstream_card_id');
      const body: Record<string, string | number> = { outcome: String(v['outcome']), reason: textVal(v, 'reason'), ...(needCard && safeId(up) ? { upstream_card_id: Number(up) } : {}) };
      const alive = sessionGuard(c);
      try {
        await c.gp.api.call(route, { path: { biz_no: row.biz_no }, body });
      } catch (e) {
        if (e instanceof ApiError && e.code === 1211 && e.data.reason === 'resolve_not_applied') throw localErr(route, str(t, 'errResolveNotApplied'));
        throw e;
      }
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastResolve'));
      await reloadCurrent(c);
    },
  };
}

async function approveAction(ctx: AdminCtx, row: AdminCardActionRow): Promise<void> {
  const alive = sessionGuard(ctx);
  try {
    await ctx.gp.api.call('POST /api/admin/card-actions/{biz_no}/approve', { path: { biz_no: row.biz_no }, body: {} });
  } catch (e) {
    if (!alive()) return;
    // 1501 = 复核人与发起人同一人（C-29）；外壳 403 拦截先 toast，此处后写覆盖为专门文案
    ctx.gp.ui.toast(e instanceof ApiError && e.code === 1501 ? str(ctx.t, 'errSameReviewer') : ctx.gp.ui.err(e));
    return;
  }
  if (!alive()) return;
  ctx.gp.ui.toast(str(ctx.t, 'toastApprove'));
  await reloadCurrent(ctx);
}

const inReview = (r: AdminCardActionRow): boolean => r.step === 'await_review' && r.status !== 'success' && r.status !== 'failed';

function cardActionRowActions(t: Dict): readonly ActionDef<AdminCardActionRow>[] {
  return [
    { label: 'aApprove', need: CARD_APPROVE, show: inReview, run: (c, r) => openConfirm(c, { title: str(t, 'caApproveTitle'), text: str(t, 'caApproveText'), label: r.biz_no, run: (cc) => approveAction(cc, r) }) },
    { label: 'aReject', need: CARD_APPROVE, danger: true, show: inReview, run: (c, r) => openForm(c, rejectForm(r, t)) },
    { label: 'aReconcile', need: CARD_EDIT, show: (r) => r.status === 'unknown', run: (c, r) => guarded(`reconcile:${r.biz_no}`, (cc) => reconcile(cc, r.biz_no, r.card_id))(c) },
    { label: 'aResolve', need: CARD_APPROVE, show: (r) => r.status === 'unknown', run: (c, r) => openForm(c, resolveForm(r, t)) },
    { label: 'aOpenCard', show: (r) => r.card_id !== null, run: (c, r) => (r.card_id === null ? undefined : openCardDrawer(c, r.card_id)) },
  ];
}

function amountSub(t: Dict, r: AdminCardActionRow): string {
  return positive(r.fee) ? `fee ${usd(r.fee)} · ${str(t, 'crdTotal')} ${usd(r.total)}` : '';
}

// 06 C-29 抽屉：字段格 + 与行内同规则的底部按钮
function openCardAction(ctx: AdminCtx, r: AdminCardActionRow): void {
  const t = ctx.t;
  const z = tz(ctx);
  const acts: DrawerAction[] = cardActionRowActions(t)
    .filter((a) => a.show?.(r) ?? true)
    .map((a) => ({ label: a.label, kind: a.danger ? ('danger' as const) : a.label === 'aApprove' ? ('green' as const) : ('' as const), ...(a.need ? { need: a.need } : {}), run: (c: AdminCtx) => a.run(c, r) }));
  openDrawer(ctx, {
    kind: str(t, 'cardActions'),
    title: `${actLabel(t, r.action)} · ${r.biz_no}`,
    subtitle: `${chipText(t, CA_STATUS, r.status)} · ${stepLabel(t, r.step)}`,
    fields: [
      { k: str(t, 'cUser'), v: userText(r.user) },
      { k: str(t, 'crdCard'), v: r.card_no ?? '—' },
      { k: str(t, 'cBudget'), v: r.budget_id === null ? '—' : `#${r.budget_id}` },
      { k: str(t, 'cAmount'), v: usd(r.amount) },
      { k: str(t, 'crdFee'), v: usd(r.fee) },
      { k: str(t, 'crdTotal'), v: usd(r.total) },
      { k: str(t, 'crdOperator'), v: `${operatorText(t, r)} · ${r.operator}` },
      { k: str(t, 'crdStep'), v: `${stepLabel(t, r.step)} · ${stepStatusLabel(t, r.step_status)}` },
      { k: str(t, 'cReason'), v: r.reason ?? '—', wide: true },
      { k: str(t, 'crdError'), v: r.error_message ?? '—', wide: true },
      { k: str(t, 'crdReviewer'), v: r.reviewed_by === null ? '—' : `#${r.reviewed_by} · ${fmtDateTime(r.reviewed_at, { tz: z })}` },
      { k: str(t, 'crdResolver'), v: r.resolved_by === null ? '—' : `#${r.resolved_by} · ${fmtDateTime(r.resolved_at, { tz: z })}` },
      { k: str(t, 'crdCreatedAt'), v: fmtDateTime(r.created_at, { tz: z }) },
      { k: str(t, 'crdFinalizedAt'), v: fmtDateTime(r.finalized_at, { tz: z }) },
    ],
    actions: [...acts, { label: 'aClose', kind: 'primary', run: (c) => closeDrawer(c) }],
  });
}

const CA_TABS = ['all', 'review', 'unknown', 'failed'] as const;

function cardActionsView(key: (typeof CA_TABS)[number]): ErasedView {
  // 06 C-29：页签以 facet 下发（不传 tab），工具条不重复渲染对应下拉
  const stepFixed = key === 'review';
  const statusFixed = key === 'unknown' || key === 'failed';
  return defineView<AdminCardActionRow>({
    key,
    source: { route: 'GET /api/admin/card-actions', tabParam: 'none', tabFacet: { review: { step: 'await_review' }, unknown: { status: 'unknown' }, failed: { status: 'failed' } } },
    echo: '/admin/card-actions',
    cols: [
      { label: 'cTime', sortKey: 'created_at' },
      { label: 'crdBizNo' },
      { label: 'crdAction', facet: 'action', facetLabel: actLabel },
      { label: 'cUser' },
      { label: 'crdCard' },
      { label: 'cAmount' },
      stepFixed ? { label: 'crdStep' } : { label: 'crdStep', facet: 'step', facetLabel: stepLabel },
      statusFixed ? { label: 'cStatus' } : { label: 'cStatus', facet: 'status', facetLabel: (t, v) => chipText(t, CA_STATUS, v) },
      { label: 'crdOperator' },
      { label: 'crdReasonErr' },
    ],
    cells: (c, r) => {
      const t = c.t;
      const step = cell(stepLabel(t, r.step), stepStatusLabel(t, r.step_status));
      const cardSub = r.card_id === null ? '' : `${r.card_id}${r.budget_id === null ? '' : ` · ${interpolate(str(t, 'crdBudgetSub'), { id: r.budget_id })}`}`;
      return [
        cell(fmtDateTime(r.created_at, { tz: tz(c) }), r.finalized_at ? fmtDateTime(r.finalized_at, { tz: tz(c) }) : ''),
        cell(r.biz_no, '', true),
        cell(actLabel(t, r.action)),
        cell(userText(r.user), r.user.uid),
        cell(r.card_no ?? '—', cardSub, true),
        cell(usd(r.amount), amountSub(t, r)),
        r.step === 'await_review' ? { ...step, style: `${step.style};color:#c47a0c` } : step,
        chipOf(chipMap(t, CA_STATUS), r.status, 'card_action.status'),
        cell(operatorText(t, r)),
        cell(r.reason ?? r.error_message ?? '—'),
      ];
    },
    open: openCardAction,
    rowActions: (c) => cardActionRowActions(c.t),
    csvName: 'card-actions',
  });
}

// ---- C-06 消费明细 ----

const TX_TABS = ['all', 'purchase', 'authorize', 'refund', 'declined', 'dispute', 'mailcode'] as const;

const signed = (x: { billing_amount: string | null; transaction_type: string | null }): string =>
  x.billing_amount === null ? '—' : `${x.transaction_type === 'Refund' ? '+' : '-'}${usd(x.billing_amount)}`;

// 外币消费原币副标题（「 · 176,000 VND」）：billing_amount 已是卡币种金额，original_* 仅外币消费时下发（2026-10-19）
const origSuffix = (x: { original_amount?: string | null; original_currency?: string | null }): string => {
  const o = fmtOriginal(x.original_amount, x.original_currency);
  return o ? ` · ${o}` : '';
};

function txDrawer(ctx: AdminCtx, d: AdminTxDetail): DrawerSpec {
  const t = ctx.t;
  const fee = d.fee_charged === null ? '—' : usd(d.fee_charged);
  const ledger = d.fee_ledger ? ` · ${interpolate(str(t, 'crdFeeLedger'), { biz_no: d.fee_ledger.biz_no, charged: usd(d.fee_ledger.charged) })}` : '';
  return {
    kind: str(t, 'tx'),
    title: d.merchant_name ?? '—',
    subtitle: userText(d.user),
    fields: [
      { k: str(t, 'cTxId'), v: d.transaction_id, wide: true },
      { k: str(t, 'cCardNo'), v: d.card_no },
      { k: str(t, 'cAmount'), v: d.billing_amount === null ? '—' : `${usd(d.billing_amount)}${origSuffix(d)}` },
      { k: str(t, 'cFee'), v: `${fee}${ledger}` },
      { k: str(t, 'cType'), v: d.transaction_type ?? '—' },
      { k: str(t, 'cStatus'), v: d.status }, // 原型 L721 抽屉状态为 PascalCase 原值
      { k: str(t, 'crossBorder'), v: str(t, d.is_cross_border ? 'crdYes' : 'crdNo') },
    ],
    // raw 入库前 card_number 已脱敏为 last4（R-10）；为空时不渲染 JSON 区
    ...(d.raw === null || d.raw === undefined ? {} : { json: JSON.stringify(d.raw, null, 2) }),
    actions: [
      { label: str(t, 'aOpen360'), kind: '', run: (c) => openUser360(c, d.user.id) },
      { label: str(t, 'aClose'), kind: 'primary', run: (c) => closeDrawer(c) },
    ],
  };
}

const txSeq = new WeakMap<object, number>();

function openTx(ctx: AdminCtx, r: AdminTxRow): void {
  const alive = sessionGuard(ctx);
  const seq = (txSeq.get(ctx.host) ?? 0) + 1;
  txSeq.set(ctx.host, seq);
  const current = (): boolean => alive() && txSeq.get(ctx.host) === seq;
  void ctx.gp.api
    .call<AdminTxDetail>('GET /api/admin/transactions/{id}', { path: { id: r.id } })
    .then((d) => {
      if (current()) openDrawer(ctx, txDrawer(ctx, d));
    })
    .catch((e: unknown) => {
      if (current()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    });
}

function txView(key: (typeof TX_TABS)[number]): ErasedView {
  return defineView<AdminTxRow>({
    key,
    source: { route: 'GET /api/admin/transactions', tabParam: 'tab' },
    echo: '/admin/transactions',
    cols: [
      { label: 'cTxId' }, { label: 'cUser' }, { label: 'cCardNo' }, { label: 'cMerchant' }, { label: 'cAmount' },
      { label: 'cType', facet: 'type' }, { label: 'cStatus', facet: 'status', facetLabel: (t, v) => chipText(t, TX_STATUS, v) }, { label: 'cTime', sortKey: 'transaction_date' },
    ],
    cells: (c, x) => [
      cell(x.transaction_id, '', true),
      cell(userText(x.user)),
      cell(x.card_no, '', true),
      cell(x.merchant_name ?? '—', `${x.merchant_country ?? ''}${x.is_cross_border ? ` · ${str(c.t, 'crossBorder')}` : ''}`),
      cell(signed(x), [fmtOriginal(x.original_amount, x.original_currency),
        x.fee_charged !== null && positive(x.fee_charged) ? `fee ${usd(x.fee_charged)}` : ''].filter((s) => s !== '').join(' · ')),
      cell(x.transaction_type ?? '—'),
      chipOf(chipMap(c.t, TX_STATUS), x.status, 'tx.status'),
      cell(fmtDateTime(x.transaction_date, { tz: tz(c) })),
    ],
    open: openTx,
    rowActions: () => [{ label: 'aDetail', run: openTx }],
    csvName: 'tx',
  });
}

// TD-849：本文件三个页共用 I18N_CARDS（同一对象引用，table 域合并时去重）
const PACK = { i18n: I18N_CARDS, rewrites: REWRITES_CARDS } as const;

export const PAGES_CARDS: PageMap = {
  cards: { page: 'cards', ...PACK, tabLabels: (t) => strArr(t, 'tabCards'), views: CARD_TABS.map(cardsView) },
  cardActions: { page: 'cardActions', ...PACK, tabLabels: (t) => strArr(t, 'tabCardActions'), views: CA_TABS.map(cardActionsView) },
  tx: { page: 'tx', ...PACK, tabLabels: (t) => strArr(t, 'tabTx'), views: TX_TABS.map(txView) },
};
